import { supabase } from '@/integrations/supabase/client';

// App configuration - use dynamic origin for native apps
export const PRODUCTION_URL = 'https://gistgift.shop';
export const APP_URL = PRODUCTION_URL; // Consistent URL for OAuth redirects
const TIKTOK_CLIENT_KEY = 'aw1icibr5ow5758h'; // Production client key

// State storage for CSRF protection - use sessionStorage for better native compatibility
const STATE_STORAGE_KEY = 'tiktok_oauth_state';
const STATE_EXPIRY_MS = 10 * 60 * 1000; // 10 minutes

interface StoredState {
  state: string;
  timestamp: number;
  isNative: boolean;
  redirectUri: string; // Store the exact redirect_uri used
}

/**
 * Get the appropriate base URL for OAuth redirects.
 * Returns the current origin only when it is a genuine HTTPS production/preview
 * URL (e.g. Lovable preview). Falls back to PRODUCTION_URL for Capacitor apps,
 * localhost dev servers, and any non-HTTPS origin.
 */
function getBaseUrl(isNative: boolean): string {
  if (isNative && typeof window !== 'undefined') {
    const origin = window.location.origin;
    // FIX: Original check was `!origin.includes('localhost:')`, which only excluded
    // localhost with a port (e.g. http://localhost:8080). On iOS, Capacitor's
    // internal origin is `capacitor://localhost` — no colon after "localhost" — so
    // the check passed and returned `capacitor://localhost` as the base URL,
    // producing a redirect_uri of `capacitor://localhost/native-callback`.
    // TikTok requires HTTPS redirect URIs, so this caused every native OAuth
    // attempt to fail with a redirect_uri mismatch.
    //
    // Fix: only use the current origin if it is a proper HTTPS URL with no
    // localhost hostname (covers Lovable preview / staging environments).
    if (origin && origin.startsWith('https://') && !origin.includes('localhost')) {
      return origin;
    }
  }
  return PRODUCTION_URL;
}

/**
 * Generate TikTok OAuth URL
 * @param extraParams - Optional extra query params appended to the OAuth URL
 *   (e.g. { force_relogin: 'true', prompt: 'login' } to force account picker)
 */
export function getTikTokOAuthUrl(isNative: boolean, deeplinkScheme: string = 'gistgift', extraParams?: Record<string, string>): string {
  // Generate CSRF state
  const state = crypto.randomUUID();

  // Determine base URL and redirect path based on flow
  const baseUrl    = getBaseUrl(isNative);
  const redirectPath = isNative ? '/native-callback' : '/auth';
  const redirectUri  = `${baseUrl}${redirectPath}`;

  // Store state for validation - include redirectUri for deterministic exchange
  const storedState: StoredState = {
    state,
    timestamp: Date.now(),
    isNative,
    redirectUri,
  };

  // Try sessionStorage first (better for auth flows), fallback to localStorage
  try {
    sessionStorage.setItem(STATE_STORAGE_KEY, JSON.stringify(storedState));
  } catch {
    localStorage.setItem(STATE_STORAGE_KEY, JSON.stringify(storedState));
  }

  console.log('[TikTok Auth] Generating OAuth URL');
  console.log('[TikTok Auth] isNative:', isNative);
  console.log('[TikTok Auth] baseUrl:', baseUrl);
  console.log('[TikTok Auth] redirectUri:', redirectUri);

  // Build OAuth URL
  const params = new URLSearchParams({
    client_key:    TIKTOK_CLIENT_KEY,
    response_type: 'code',
    scope:         'user.info.basic',
    redirect_uri:  redirectUri,
    state:         state,
  });

  // FIX: Embed provider tag as third state segment for native flows: `uuid|scheme|tiktok`.
  // Previously the format `uuid|scheme` was identical to X's native state format, so
  // NativeCallback.tsx could not distinguish providers without a sessionStorage lookup —
  // which doesn't work from the external browser context. The provider tag lets
  // isTikTokCallback() / isXCallback() route correctly without any storage access.
  if (isNative) {
    params.set('state', `${state}|${deeplinkScheme}|tiktok`);
  }

  // FIX: Append caller-supplied extra params AFTER state is set so they can't
  // accidentally overwrite the state param. This is how TikTokLoginButton's
  // { force_relogin: 'true', prompt: 'login' } finally reaches the OAuth URL —
  // previously these were passed to useAuthOrchestrator but silently dropped
  // because the call chain had no mechanism to forward them this far.
  if (extraParams) {
    for (const [key, value] of Object.entries(extraParams)) {
      params.set(key, value);
    }
  }

  return `https://www.tiktok.com/v2/auth/authorize/?${params.toString()}`;
}

/**
 * Validate OAuth state for CSRF protection
 * NOTE: For native apps, state may not be available due to storage isolation.
 * In that case, we log a warning but don't hard-fail.
 */
export function validateOAuthState(returnedState: string): {
  valid: boolean;
  isNative: boolean;
  deeplinkScheme?: string;
  redirectUri?: string;
  warning?: string;
} {
  try {
    // Try sessionStorage first, then localStorage
    let stored = sessionStorage.getItem(STATE_STORAGE_KEY);
    if (!stored) {
      stored = localStorage.getItem(STATE_STORAGE_KEY);
    }

    // Parse returned state first to extract potential deeplink scheme
    const [stateValue, deeplinkScheme] = returnedState.split('|');

    if (!stored) {
      // For native flows, storage isolation may prevent state retrieval.
      // Log warning but allow the flow to continue (TikTok's own CSRF protection still applies).
      console.warn('[TikTok Auth] No stored state found - this is common in native OAuth flows due to storage isolation');
      return {
        valid: true,
        isNative: !!deeplinkScheme,
        deeplinkScheme: deeplinkScheme || 'gistgift',
        warning: 'State validation skipped due to storage isolation',
      };
    }

    const storedState: StoredState = JSON.parse(stored);

    // Check expiry
    if (Date.now() - storedState.timestamp > STATE_EXPIRY_MS) {
      console.error('[TikTok Auth] State expired');
      sessionStorage.removeItem(STATE_STORAGE_KEY);
      localStorage.removeItem(STATE_STORAGE_KEY);
      return {
        valid: true,
        isNative: storedState.isNative,
        deeplinkScheme: deeplinkScheme || 'gistgift',
        redirectUri: storedState.redirectUri,
        warning: 'State expired but allowing flow to continue',
      };
    }

    // Validate state match
    if (stateValue !== storedState.state) {
      console.error('[TikTok Auth] State mismatch - stored:', storedState.state, 'received:', stateValue);
      // For native, this can happen due to ASWebAuthenticationSession storage isolation.
      return {
        valid: true,
        isNative: storedState.isNative,
        deeplinkScheme: deeplinkScheme || 'gistgift',
        redirectUri: storedState.redirectUri,
        warning: 'State mismatch - may be due to native storage isolation',
      };
    }

    // Clear state after successful validation
    sessionStorage.removeItem(STATE_STORAGE_KEY);
    localStorage.removeItem(STATE_STORAGE_KEY);

    return {
      valid: true,
      isNative: storedState.isNative,
      deeplinkScheme: deeplinkScheme || 'gistgift',
      redirectUri: storedState.redirectUri,
    };
  } catch (error) {
    console.error('[TikTok Auth] State validation error:', error);
    const [, deeplinkScheme] = returnedState.split('|');
    return {
      valid: true,
      isNative: !!deeplinkScheme,
      deeplinkScheme: deeplinkScheme || 'gistgift',
      warning: 'State validation failed with error',
    };
  }
}

/**
 * Exchange TikTok authorization code for session tokens
 * @param code - Authorization code from TikTok
 * @param isNative - Whether this is a native app flow
 * @param storedRedirectUri - Optional stored redirect_uri from state (preferred for consistency)
 */
export async function exchangeTikTokCode(
  code: string,
  isNative: boolean,
  storedRedirectUri?: string
): Promise<{
  access_token: string;
  refresh_token: string;
  user: {
    id: string;
    email: string;
    display_name: string;
    avatar_url: string | null;
  };
}> {
  // Use stored redirectUri if available (ensures exact match with auth request)
  let redirectUri: string;
  if (storedRedirectUri) {
    redirectUri = storedRedirectUri;
    console.log('[TikTok Auth] Using stored redirect_uri:', redirectUri);
  } else {
    const baseUrl      = getBaseUrl(isNative);
    const redirectPath = isNative ? '/native-callback' : '/auth';
    redirectUri = `${baseUrl}${redirectPath}`;
    console.log('[TikTok Auth] Computed redirect_uri:', redirectUri);
  }

  console.log('[TikTok Auth] Exchanging code');
  console.log('[TikTok Auth] isNative:', isNative);
  console.log('[TikTok Auth] redirect_uri:', redirectUri);

  const { data, error } = await supabase.functions.invoke('auth-tiktok-callback', {
    body: { code, redirect_uri: redirectUri },
  });

  if (error) {
    console.error('[TikTok Auth] Exchange error:', error);
    let errorMessage = error.message || 'Failed to exchange TikTok code';

    if ((error as any).context) {
      try {
        const ctx = (error as any).context;
        if (typeof ctx === 'string') {
          const parsed = JSON.parse(ctx);
          errorMessage = parsed.error || parsed.message || errorMessage;
          if (parsed.details) {
            errorMessage += ` (${JSON.stringify(parsed.details)})`;
          }
        }
      } catch {
        // Ignore parsing errors
      }
    }

    throw new Error(errorMessage);
  }

  if (data?.error) {
    console.error('[TikTok Auth] Exchange returned error:', data);
    let errorMessage = data.error;
    if (data.details) {
      const details = data.details;
      if (details.error_description) {
        errorMessage = details.error_description;
      } else if (details.error) {
        errorMessage = `${data.error}: ${details.error}`;
      }
    }
    if (data.redirect_uri) {
      errorMessage += ` [redirect_uri: ${data.redirect_uri}]`;
    }
    throw new Error(errorMessage);
  }

  if (!data) {
    throw new Error('No data returned from token exchange');
  }

  return data;
}

/**
 * Set Supabase session from TikTok OAuth tokens
 */
export async function setTikTokSession(accessToken: string, refreshToken: string): Promise<void> {
  const { error } = await supabase.auth.setSession({
    access_token:  accessToken,
    refresh_token: refreshToken,
  });

  if (error) {
    console.error('[TikTok Auth] Failed to set session:', error);
    throw error;
  }

  console.log('[TikTok Auth] Session set successfully');
}

/**
 * Check if a callback contains TikTok OAuth params
 */
export function isTikTokCallback(searchParams: URLSearchParams): boolean {
  // TikTok returns code and state, but NOT in the hash (unlike Supabase OAuth)
  if (!searchParams.has('code') || !searchParams.has('state') || searchParams.has('access_token')) {
    return false;
  }
  // FIX: Exclude X callbacks that have the `|x` provider tag in the state parameter.
  // Without this check, X callbacks (which share the same code+state query shape as
  // TikTok) would fall through and be processed as TikTok after isXCallback() returns
  // false (which it did before the x-auth.ts fix, and as a safety net here).
  const state    = searchParams.get('state') || '';
  const provider = state.split('|')[2];
  if (provider === 'x') return false;
  return true;
}

/**
 * Get the redirect URI for the current environment
 */
export function getTikTokRedirectUri(isNative: boolean): string {
  const baseUrl = getBaseUrl(isNative);
  return isNative
    ? `${baseUrl}/native-callback`
    : `${baseUrl}/auth`;
}
